import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../database/config';
import { getPostSlug } from '../../utils/slugify';

// Category colors
const categoryColors = {
    "Health": "bg-green-100 text-green-700",
    "Lifestyle": "bg-blue-100 text-blue-700",
    "Food Safety": "bg-red-100 text-red-700",
    "Environment": "bg-emerald-100 text-emerald-700",
    "Recipes": "bg-orange-100 text-orange-700",
    "Tips": "bg-purple-100 text-purple-700",
    "Stories": "bg-pink-100 text-pink-700",
    "Food": "bg-amber-100 text-amber-700",
    "Technology": "bg-indigo-100 text-indigo-700",
    "default": "bg-orange-100 text-orange-700"
};

const BlogCard = ({ post }) => {
    const categoryClass = categoryColors[post.type] || categoryColors["default"];
    const postSlug = getPostSlug(post);

    return (
        <Link to={`/blog/${postSlug}`} className="block">
            <article className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group">
                {/* Image */}
                <div className="relative overflow-hidden">
                    <img
                        src={post.img}
                        alt={post.alt}
                        title={post.title}
                        loading="lazy"
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                </div>

                {/* Content */}
                <div className="p-5">
                    {/* Category */}
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${categoryClass}`}>
                        {post.type}
                    </span>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-orange-500 transition-colors">
                        {post.heading}
                    </h3>

                    {/* Author & Date */}
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden">
                            {post.authorImg ? (
                                <img src={post.authorImg} alt={post.alt} title={post.title} loading="lazy" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-orange-500 text-sm font-semibold">{post.author?.charAt(0) || 'A'}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span className="font-medium text-gray-700">{post.author}</span>
                            <span>•</span>
                            <span>{post.date}</span>
                        </div>
                    </div>
                </div>
            </article>
        </Link>
    );
};

const FeaturedPost = ({ post }) => {
    const postSlug = getPostSlug(post);

    return (
        <Link to={`/blog/${postSlug}`} className="block">
            <article className="relative rounded-2xl overflow-hidden shadow-xl group cursor-pointer">
                {/* Background Image */}
                <div className="relative h-80 md:h-96 lg:h-[450px]">
                    <img
                        src={post.img}
                        alt={post.alt}
                        title={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    {/* Category */}
                    <span className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-4 bg-orange-500 text-white">
                        {post.type}
                    </span>

                    {/* Title */}
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 max-w-3xl">
                        {post.heading}
                    </h2>

                    {/* Author & Date */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden border-2 border-white">
                            {post.authorImg ? (
                                <img src={post.authorImg} alt={post.alt} title={post.title} loading="lazy" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-orange-500 text-sm font-semibold">{post.author?.charAt(0) || 'A'}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-white/90">
                            <span className="font-medium">{post.author}</span>
                            <span>•</span>
                            <span>{post.date}</span>
                        </div>
                    </div>
                </div>
            </article>
        </Link>
    );
};

const Blog = ({ preloadedData }) => {
    const posts = preloadedData?.posts || [];
    const pageTitle = preloadedData?.pageTitle || 'Blog';
    const [visiblePosts, setVisiblePosts] = useState(6);
    const [loadingMore, setLoadingMore] = useState(false);

    const orderedPosts = [...posts].sort((a, b) => {
        const numA = parseInt(a.id.replace('card', '')) || 0;
        const numB = parseInt(b.id.replace('card', '')) || 0;
        return numB - numA;
    });

    const featuredPost = orderedPosts[0];
    const regularPosts = orderedPosts.slice(1);

    const handleLoadMore = () => {
        setLoadingMore(true);
        setTimeout(() => {
            setVisiblePosts(prev => prev + 6);
            setLoadingMore(false);
        }, 500);
    };


    return (
        <section id="blog" className="!pt-12 md:!pt-16 bg-gradient-to-b from-gray-50/50 to-white min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pt-12 md:pb-24">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
                        {pageTitle}
                    </h1>
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                        <a href="/" className="hover:text-orange-500 transition-colors">Home</a>
                        <span>/</span>
                        <span className="text-gray-700">Blog</span>
                    </div>
                </div>

                {/* Featured Post */}
                {featuredPost && (
                    <div className="mb-12">
                        <FeaturedPost post={featuredPost} />
                    </div>
                )}

                {/* Blog Grid */}
                {regularPosts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {regularPosts.slice(0, visiblePosts).map((post) => (
                            <BlogCard key={post.id} post={post} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 text-gray-500">
                        No blog posts available yet.
                    </div>
                )}

                {/* Load More Button */}
                {visiblePosts < regularPosts.length && (
                    <div className="text-center mt-12">
                        <button
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            className="inline-flex items-center justify-center px-8 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:border-orange-500 hover:text-orange-500 transition-all duration-300 disabled:opacity-50"
                        >
                            {loadingMore ? (
                                <span className="flex items-center gap-2">
                                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                </span>
                            ) : (
                                'Load More'
                            )}
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default Blog;
